import * as React from "react";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";
import axios from "axios";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  useCustomerCartId_asController,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useDataSourceMyBagTotalsController } from "../data-source--mybag-totals-provider";

import { DataSource_MyBag_QueryKey } from "./data-source--mybag-query-key-factory";

interface DataSourceMyBagQueryConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<CheckoutModel_MyBag> {
  data: UseQueryResult<CheckoutModel_MyBag>["data"];
  status: UseQueryResult<CheckoutModel_MyBag>["status"];
}

const defaultValue = {} as unknown as DataSourceMyBagQueryConsumerInterface;

const DataSourceMyBagQueryContext = React.createContext(defaultValue);

export interface InitialStateMinimumRequirements_MyBag_DataSource
  extends InitialStateMinimumRequirements_GraphQl {}

export interface DataSourceMyBagQueryProviderProps {
  isCustomerLoggedIn: boolean;
  cartId?: string;

  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_MyBag_DataSource;
  };
}
export const DataSourceMyBagQueryProvider: React.FunctionComponent<
  DataSourceMyBagQueryProviderProps
> = (props) => {
  const { handleNewCustomerCartId } = useCustomerCartId_asController();
  const { invalidate: dataSourceMyBagTotals_invalidate } =
    useDataSourceMyBagTotalsController();

  const { initialState } = props.useInitialState();

  const queryKey = React.useMemo(() => {
    return DataSource_MyBag_QueryKey.mybag(props);
  }, [props, props.cartId, props.isCustomerLoggedIn]);

  const queryFn: ReactQuery.QueryFunction<
    CheckoutModel_MyBag,
    typeof queryKey
  > = async (context: ReactQuery.QueryFunctionContext<typeof queryKey>) => {
    const [baseQuery, desiredBag] = context.queryKey;

    const isCustomerLoggedIn = desiredBag.isCustomerLoggedIn;
    const cartId = desiredBag.cartId;

    if (!isCustomerLoggedIn && !cartId) {
      throw new Error("Cannot request mybag data without a cartId.");
    }

    let gqlQuery = `{
      cart(cart_id: "${cartId}") {
        ${CheckoutModel_MyBag_Graphql_Request}
      }
    }`;
    if (isCustomerLoggedIn) {
      gqlQuery = `{
        customerCart {
          ${CheckoutModel_MyBag_Graphql_Request}
        }
      }`;
    }

    const headers = {
      "Content-Type": "application/json",
      Store: initialState.store_code,
    };

    const { data } = await axios.post<
      GraphqlWrappedData<"cart" | "customerCart", CheckoutModel_MyBag>
    >("/graphql", { query: gqlQuery }, { headers });

    const { data: gqlResponseData } = data || {};
    const { cart, customerCart } = gqlResponseData || {};

    return customerCart || cart;
  };

  const handleSuccess = React.useCallback(
    (data: CheckoutModel_MyBag) => {
      handleNewCustomerCartId(data?.id);
      dataSourceMyBagTotals_invalidate();
    },
    [handleNewCustomerCartId, dataSourceMyBagTotals_invalidate]
  );

  const { status, data, dataUpdatedAt, errorUpdatedAt, error, ...result } =
    ReactQuery.useQuery(queryKey, queryFn, {
      onSuccess: handleSuccess,
      refetchOnWindowFocus: false,
    });

  const value: DataSourceMyBagQueryConsumerInterface = React.useMemo(
    () => ({
      status,
      data,
      dataUpdatedAt,
      errorUpdatedAt,
      error,
    }),
    [data, dataUpdatedAt, errorUpdatedAt, status, error]
  );
  return (
    <DataSourceMyBagQueryContext.Provider value={value}>
      {props?.children || []}
    </DataSourceMyBagQueryContext.Provider>
  );
};

export const useDataSourceMyBagQuery = () => {
  const context = React.useContext(DataSourceMyBagQueryContext);

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceMyBag must be used within a DataSourceMyBagQueryProvider"
  //   );
  // }

  return context;
};
